import { useEffect, useRef, useState } from 'react';
import { useSync } from '../../sync';
import { useT } from '../../i18n';
import { cx } from './lib/classNames';
import styles from './SyncErrorBanner.module.css';

// N-17: Die SyncEngine kennt einen eigenen Zustand status==='error' fuer
// wiederholt fehlschlagendes Hintergrund-Polling (engine.ts runPoll-catch),
// aber bisher liest KEINE UI-Komponente ihn — nur OfflineBanner/useOnline()
// misst die reine Geraete-Netzwerkverbindung und bleibt online, waehrend im
// Hintergrund seit Minuten nichts mehr synchronisiert. Dieser Banner zeigt
// nach PERSISTENT_ERROR_MS einen dezenten Hinweis mit "Erneut versuchen"
// (forceSync). Muster: OfflineBanner.
const PERSISTENT_ERROR_MS = 60_000;

export interface SyncErrorBannerProps {
  className?: string;
}

export function SyncErrorBanner({ className }: SyncErrorBannerProps) {
  const { status, forceSync } = useSync();
  const { t } = useT();
  const [visible, setVisible] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (status !== 'error') {
      setVisible(false);
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }
    timerRef.current = setTimeout(() => setVisible(true), PERSISTENT_ERROR_MS);
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [status]);

  if (!visible) return null;

  const handleRetry = () => {
    setRetrying(true);
    void forceSync().finally(() => setRetrying(false));
  };

  return (
    <div className={cx(styles.root, className)} role="status">
      <span>{t('sync.error_persistent')}</span>
      {/* chat.retry ("Erneut versuchen") ist der bestehende, app-weite
          Retry-Text (u.a. Tindra) — kein neuer Key fuer dieselbe Aktion. */}
      <button type="button" className={styles.retry} onClick={handleRetry} disabled={retrying}>
        {t('chat.retry')}
      </button>
    </div>
  );
}
